import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";

const SIGNAL_OPTIONS = [
  { id: "pre_foreclosure", label: "Pre-foreclosure" },
  { id: "code_violation", label: "Code violation" },
  { id: "recent_eviction", label: "Recent eviction" },
  { id: "tax_delinquent", label: "Tax delinquent" },
  { id: "absentee_owner", label: "Absentee owner" },
  { id: "vacant", label: "Vacant" },
  { id: "probate", label: "Probate" },
  { id: "high_equity", label: "High equity" },
  { id: "long_term_owner", label: "Long-term owner" },
];

const TYPE_OPTIONS = ["Single Family", "Duplex", "Triplex", "Townhouse", "Small Multi-Family", "Land"];

function emptyForm() {
  return {
    name: "",
    email: "",
    phone: "",
    notes: "",
    states: "",
    cities: "",
    property_types: [],
    must_have_signals: [],
    min_price: "",
    max_price: "",
    min_arv: "",
    max_arv: "",
    max_repairs: "",
    min_potential_profit: "",
    min_owner_equity_pct: "",
    min_beds: "",
    max_beds: "",
    min_baths: "",
    max_baths: "",
    channels: { email: true, sms: true },
    active: true,
  };
}

function fromBuyer(b) {
  return {
    ...emptyForm(),
    ...b,
    name: b.name ?? "",
    email: b.email ?? "",
    phone: b.phone ?? "",
    notes: b.notes ?? "",
    states: (b.states || []).join(", "),
    cities: (b.cities || []).join(", "),
    property_types: b.property_types || [],
    must_have_signals: b.must_have_signals || [],
    channels: b.channels || { email: true, sms: true },
    min_price: b.min_price ?? "",
    max_price: b.max_price ?? "",
    min_arv: b.min_arv ?? "",
    max_arv: b.max_arv ?? "",
    max_repairs: b.max_repairs ?? "",
    min_potential_profit: b.min_potential_profit ?? "",
    min_owner_equity_pct: b.min_owner_equity_pct ?? "",
    min_beds: b.min_beds ?? "",
    max_beds: b.max_beds ?? "",
    min_baths: b.min_baths ?? "",
    max_baths: b.max_baths ?? "",
  };
}

function toPayload(form) {
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const str = (v) => (v ?? "").toString().trim();
  return {
    name: str(form.name),
    email: str(form.email) || null,
    phone: str(form.phone) || null,
    notes: str(form.notes),
    states: str(form.states).split(",").map((s) => s.trim()).filter(Boolean),
    cities: str(form.cities).split(",").map((s) => s.trim()).filter(Boolean),
    property_types: form.property_types,
    must_have_signals: form.must_have_signals,
    min_price: num(form.min_price),
    max_price: num(form.max_price),
    min_arv: num(form.min_arv),
    max_arv: num(form.max_arv),
    max_repairs: num(form.max_repairs),
    min_potential_profit: num(form.min_potential_profit),
    min_owner_equity_pct: num(form.min_owner_equity_pct),
    min_beds: num(form.min_beds),
    max_beds: num(form.max_beds),
    min_baths: num(form.min_baths),
    max_baths: num(form.max_baths),
    channels: form.channels,
    active: form.active,
  };
}

export default function BuyerFormModal({ buyer, onClose, onSubmit }) {
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(buyer ? fromBuyer(buyer) : emptyForm());
  }, [buyer]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (key, val) =>
    setForm((f) => {
      const has = f[key].includes(val);
      return { ...f, [key]: has ? f[key].filter((x) => x !== val) : [...f[key], val] };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Add an email or phone so you can blast deals");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(toPayload(form));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="buyer-form-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-[--ink] animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[--line] flex items-center justify-between">
          <div>
            <div className="overline">// {buyer ? "Edit" : "New"} buyer</div>
            <h2 className="font-display text-2xl font-bold tracking-tighter mt-1">
              {buyer ? buyer.name : "Add a cash buyer"}
            </h2>
          </div>
          <button
            type="button"
            data-testid="close-buyer-form"
            onClick={onClose}
            className="p-2 hover:bg-[--bg-soft] border border-[--line]"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-5 text-sm">
          <Field label="Name *">
            <input
              data-testid="buyer-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              className="w-full"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="buyer-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="w-full"
            />
          </Field>
          <Field label="Phone">
            <input
              data-testid="buyer-phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="w-full"
              placeholder="+1 555 555 0123"
            />
          </Field>
          <Field label="Notes">
            <input
              data-testid="buyer-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="w-full"
            />
          </Field>

          <div className="md:col-span-2 border-t border-[--line] pt-5">
            <div className="overline mb-3">Buy box · location</div>
          </div>
          <Field label="States (comma-sep, e.g. TX, OK)">
            <input
              data-testid="buyer-states"
              value={form.states}
              onChange={(e) => set("states", e.target.value)}
              className="w-full font-mono"
              placeholder="TX, OK"
            />
          </Field>
          <Field label="Cities (optional, comma-sep)">
            <input
              data-testid="buyer-cities"
              value={form.cities}
              onChange={(e) => set("cities", e.target.value)}
              className="w-full font-mono"
              placeholder="Dallas, Austin"
            />
          </Field>

          <div className="md:col-span-2">
            <div className="overline mb-2">Property types</div>
            <div className="flex flex-wrap gap-1.5">
              {TYPE_OPTIONS.map((t) => {
                const on = form.property_types.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`buyer-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggleArr("property_types", t)}
                    className={`text-xs px-3 py-1.5 border ${
                      on
                        ? "bg-[--ink] text-white border-[--ink]"
                        : "bg-white text-[--ink] border-[--line] hover:border-[--ink]"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2 border-t border-[--line] pt-5">
            <div className="overline mb-3">Buy box · numbers</div>
          </div>
          <Field label="Min asking ($)">
            <input
              data-testid="buyer-min-price"
              type="number"
              value={form.min_price}
              onChange={(e) => set("min_price", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Max asking ($)">
            <input
              data-testid="buyer-max-price"
              type="number"
              value={form.max_price}
              onChange={(e) => set("max_price", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Min ARV ($)">
            <input
              data-testid="buyer-min-arv"
              type="number"
              value={form.min_arv}
              onChange={(e) => set("min_arv", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Max ARV ($)">
            <input
              data-testid="buyer-max-arv"
              type="number"
              value={form.max_arv}
              onChange={(e) => set("max_arv", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Max repairs ($)">
            <input
              data-testid="buyer-max-repairs"
              type="number"
              value={form.max_repairs}
              onChange={(e) => set("max_repairs", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Min profit ($)">
            <input
              data-testid="buyer-min-profit"
              type="number"
              value={form.min_potential_profit}
              onChange={(e) => set("min_potential_profit", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Min equity %">
            <input
              data-testid="buyer-min-equity"
              type="number"
              value={form.min_owner_equity_pct}
              onChange={(e) => set("min_owner_equity_pct", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Min beds">
            <input
              data-testid="buyer-min-beds"
              type="number"
              value={form.min_beds}
              onChange={(e) => set("min_beds", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Max beds">
            <input
              data-testid="buyer-max-beds"
              type="number"
              value={form.max_beds}
              onChange={(e) => set("max_beds", e.target.value)}
              className="w-full font-mono"
            />
          </Field>
          <Field label="Min baths">
            <input
              data-testid="buyer-min-baths"
              type="number"
              step="0.5"
              value={form.min_baths}
              onChange={(e) => set("min_baths", e.target.value)}
              className="w-full font-mono"
            />
          </Field>

          <div className="md:col-span-2">
            <div className="overline mb-2">Must-have distress signals (any)</div>
            <div className="flex flex-wrap gap-1.5">
              {SIGNAL_OPTIONS.map((s) => {
                const on = form.must_have_signals.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`buyer-signal-${s.id}`}
                    onClick={() => toggleArr("must_have_signals", s.id)}
                    className={`text-xs px-3 py-1.5 border ${
                      on
                        ? "bg-[--accent] text-white border-[--accent]"
                        : "bg-white text-[--ink] border-[--line] hover:border-[--ink]"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2 border-t border-[--line] pt-5">
            <div className="overline mb-3">Delivery</div>
            <div className="flex items-center gap-5 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="buyer-channel-email"
                  checked={!!form.channels.email}
                  onChange={(e) =>
                    set("channels", { ...form.channels, email: e.target.checked })
                  }
                />
                Email
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="buyer-channel-sms"
                  checked={!!form.channels.sms}
                  onChange={(e) =>
                    set("channels", { ...form.channels, sms: e.target.checked })
                  }
                />
                SMS
              </label>
              <label className="flex items-center gap-2 ml-auto">
                <input
                  type="checkbox"
                  data-testid="buyer-active"
                  checked={!!form.active}
                  onChange={(e) => set("active", e.target.checked)}
                />
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="border-t border-[--line] p-4 flex justify-end gap-2">
          <button
            type="button"
            data-testid="cancel-buyer-form"
            onClick={onClose}
            className="btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="submit-buyer-form"
            disabled={busy}
            className="btn-primary"
          >
            {busy ? "Saving…" : buyer ? "Save changes" : "Add buyer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[--ink-muted] block mb-1">{label}</span>
      {children}
    </label>
  );
}
